import { createStore } from 'zustand-x';
import jwtDecode from 'jwt-decode';
import { StateStorage, createJSONStorage } from 'zustand/middleware';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { Capacitor } from '@capacitor/core';

const mySecureStorage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    try {
      const result = await SecureStoragePlugin.get({ key: name });
      return result?.value || null;
    } catch (error) {
      console.error('Error retrieving secure item:', error);
      return null;
    }
  },
  setItem: async (name: string, value: string): Promise<void> => {
    try {
      await SecureStoragePlugin.set({ key: name, value });
    } catch (error) {
      console.error('Error storing secure item:', error);
      throw error;
    }
  },
  removeItem: async (name: string): Promise<void> => {
    try {
      await SecureStoragePlugin.remove({ key: name });
    } catch (error) {
      console.error('Error removing secure item:', error);
    }
  },
};

interface AuthStore {
  tokens:
    | {
        access: string;
        refresh: string;
      }
    | undefined;
  isLogged: boolean;
  claims: Claims | undefined;
  isUserLogOut: boolean;
}

export type Claims = {
  id: string;
  role: string;
  orgId: string;
};

interface JwtPayload extends Claims {
  expires: number;
}

export const authStore = createStore('auth')<AuthStore>(
  {
    isLogged: false,
    tokens: undefined,
    claims: undefined,
    isUserLogOut: false,
  },
  {
    devtools: { enabled: true },
    persist: {
      enabled: true, // Enable persistence for both web and native
      name: 'eco-route-tokens',
      // Conditionally use the custom secure storage or localStorage wrapped in createJSONStorage
      storage: Capacitor.isNativePlatform()
        ? createJSONStorage(() => mySecureStorage)
        : createJSONStorage(() => localStorage),
    },
  }
).extendActions((set) => ({
  setTokens: async (accessToken: string, refreshToken: string) => {
    if (accessToken === '' || refreshToken === '') {
      throw new Error('Empty access/refresh tokens');
    }

    const claims = jwtDecode<JwtPayload>(accessToken);

    set.state((state) => ({
      ...state,
      tokens: { access: accessToken, refresh: refreshToken },
      claims,
      isLogged: true,
      isUserLogOut: false,
    }));
  },

  removeTokens: async () => {
    set.state(() => ({
      isUserLogOut: false,
      isLogged: false,
      tokens: undefined,
      claims: undefined,
    }));
  },

  logoutUser: async () => {
    set.state(() => ({
      isUserLogOut: true,
      isLogged: false,
      tokens: undefined,
      claims: undefined,
    }));
  },
}));
